<script setup lang="ts">
import { computed, ref } from 'vue'
import { api } from '../api/client'
import PageBody from '../components/PageBody.vue'
import PageHeader from '../components/PageHeader.vue'
import { IconChevronLeft, IconChevronRight, IconRefresh } from '../components/Icons'
import { Table, Td, Th, Tr } from '../components/Table'
import { Button, EmptyState, ErrorState, Panel, SkeletonRows } from '../components/ui'
import { actionName, formatDateTime, relativeTime, targetLabel } from '../lib/format'
import { useAsync, useTitle } from '../lib/hooks'

const PAGE_SIZE = 50

useTitle('日志')
const offset = ref(0)
const { data, error, initial, loading, reload } = useAsync(
  () => api.listAudit(PAGE_SIZE, offset.value),
  () => [offset.value],
)

const entries = computed(() => data.value?.entries ?? [])
const total = computed(() => data.value?.total ?? 0)
const from = computed(() => (total.value === 0 ? 0 : offset.value + 1))
const to = computed(() => offset.value + entries.value.length)
</script>

<template>
  <PageHeader title="日志" subtitle="管理员操作审计">
    <template #actions>
      <Button size="md" class="!text-[14px]" :loading="loading && !initial" @click="reload">
        <IconRefresh :size="16" />
        刷新
      </Button>
    </template>
  </PageHeader>

  <PageBody>
    <Panel :title="`共 ${total} 条`" :hint="total > 0 ? `显示 ${from}–${to}` : undefined">
      <ErrorState v-if="error" :message="error.message" @retry="reload" />
      <SkeletonRows v-else-if="initial" :rows="8" :cols="4" />
      <EmptyState
        v-else-if="entries.length === 0"
        title="还没有审计记录"
        hint="管理员登录后台、调整权益后，这里会留下痕迹。"
      />
      <template v-else>
        <Table>
          <template #head>
            <Tr>
              <Th class="w-[24%]">时间</Th>
              <Th class="w-[28%]">操作人</Th>
              <Th class="w-[24%]">操作</Th>
              <Th class="w-[24%]">对象</Th>
            </Tr>
          </template>
          <Tr v-for="entry in entries" :key="entry.id">
            <Td>
              <span class="block text-[14px] text-text">{{ formatDateTime(entry.created_at) }}</span>
              <span class="mt-0.5 block text-[12.5px] text-dim">{{ relativeTime(entry.created_at) }}</span>
            </Td>
            <Td>
              <span class="block truncate text-[14px]" :title="entry.actor_email">
                {{ entry.actor_email || '—' }}
              </span>
            </Td>
            <Td>
              <span class="text-[14px] font-medium text-text">{{ actionName(entry.action) }}</span>
            </Td>
            <Td>
              <span class="block truncate text-[14px] text-muted" :title="entry.target_id || entry.target_type">
                {{ targetLabel(entry.target_type, entry.target_id) }}
              </span>
            </Td>
          </Tr>
        </Table>

        <div v-if="offset > 0 || to < total" class="flex items-center justify-between border-t border-line px-4 py-3">
          <span class="tnum text-[12px] text-dim">{{ from }}–{{ to }} / {{ total }}</span>
          <span class="flex gap-2">
            <Button size="sm" :disabled="offset === 0" @click="offset = Math.max(0, offset - PAGE_SIZE)">
              <IconChevronLeft :size="13" />
              上一页
            </Button>
            <Button size="sm" :disabled="to >= total" @click="offset = offset + PAGE_SIZE">
              下一页
              <IconChevronRight :size="13" />
            </Button>
          </span>
        </div>
      </template>
    </Panel>
  </PageBody>
</template>
